/*
 * <lab-shipping-estimate>
 *
 * Usato da blocks/lab-shipping-estimate.liquid (scheda prodotto) e da sections/lab-cart-slide.liquid.
 * Il markup deve contenere [data-lab-shipping-estimate-date]: la data viene scritta li dentro.
 *
 * Turns a static availability label into a delivery estimate by counting a
 * number of working days forward from the order date, skipping weekends and
 * public holidays. Orders placed after the cutoff hour count from the next day.
 *
 * Everything is computed in the shop's timezone (not the visitor's), so the
 * cutoff means "14:00 in the warehouse" wherever the customer happens to be.
 */
class LabShippingEstimate extends HTMLElement {
  constructor() {
    super();

    this.holidayCache = {};
    this.lastText = '';
    this.observer = null;
    this.render = this.render.bind(this);
    this.restore = this.restore.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
  }

  connectedCallback() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // An element created by the parser is connected before its own children
    // have been parsed, so there is nothing to fill in yet.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.render, { once: true });
    } else {
      this.render();
    }

    // Quando Horizon aggiorna la scheda prodotto (cambio variante) fa un "morph" del DOM: il nodo della data
    // resta lo stesso ma il testo torna a quello del server ("\u2014"). Qui si riscrive la data calcolata.
    if (typeof MutationObserver !== 'undefined' && !this.observer) {
      this.observer = new MutationObserver(this.restore);
      this.observer.observe(this, { childList: true, subtree: true, characterData: true });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('DOMContentLoaded', this.render);
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  restore() {
    if (!this.lastText) return;
    const dateElements = this.querySelectorAll('[data-lab-shipping-estimate-date]');
    for (const element of dateElements) {
      if (element.textContent !== this.lastText) {
        this.render();
        return;
      }
    }
  }

  // A page left open overnight, or across the cutoff, would otherwise keep
  // showing the estimate it was rendered with.
  onVisibilityChange() {
    if (!document.hidden) this.render();
  }

  render() {
    // Queried here rather than in the constructor: a parser created element
    // has no children yet by the time the constructor runs.
    const dateElements = this.querySelectorAll('[data-lab-shipping-estimate-date]');
    if (!dateElements.length) return;

    const deliveryDate = this.deliveryDate();
    const formatted = this.formatDate(deliveryDate);
    this.lastText = formatted;

    dateElements.forEach((element) => {
      element.textContent = formatted;
    });

    this.setAttribute('data-estimated-date', this.isoDate(deliveryDate));
  }

  get leadDays() {
    const days = parseInt(this.dataset.leadDays, 10);
    return isNaN(days) ? 2 : Math.max(0, days);
  }

  get cutoffHour() {
    const hour = parseInt(this.dataset.cutoffHour, 10);
    return isNaN(hour) ? 14 : hour;
  }

  get cutoffMinute() {
    const minute = parseInt(this.dataset.cutoffMinute, 10);
    return isNaN(minute) ? 0 : minute;
  }

  get timezone() {
    return this.dataset.timezone || 'Europe/Rome';
  }

  get dayLabels() {
    // Labels are authored Monday-first for readability; Date works Sunday-first.
    const labels = this.labelsFrom(this.dataset.dayLabels, LabShippingEstimate.DAY_LABELS);
    return [labels[6]].concat(labels.slice(0, 6));
  }

  get monthLabels() {
    return this.labelsFrom(this.dataset.monthLabels, LabShippingEstimate.MONTH_LABELS);
  }

  labelsFrom(value, fallback) {
    const labels = (value || '')
      .split(',')
      .map((label) => label.trim())
      .filter((label) => label.length);

    return labels.length === fallback.length ? labels : fallback;
  }

  /*
   * An order placed before the cutoff on a working day leaves the same day,
   * otherwise it leaves on the next working day. The lead time is then counted
   * in working days from that dispatch day, so an order taken on Monday
   * morning with a two day lead time arrives on Wednesday, while the same
   * order taken on Saturday only leaves on Monday and arrives on Wednesday.
   */
  deliveryDate() {
    const now = this.nowInShopTimezone();
    const date = new Date(Date.UTC(now.year, now.month - 1, now.day));

    if (now.hour * 60 + now.minute >= this.cutoffHour * 60 + this.cutoffMinute) {
      date.setUTCDate(date.getUTCDate() + 1);
    }

    // Nothing leaves the warehouse on a weekend or a public holiday.
    while (!this.isWorkingDay(date)) {
      date.setUTCDate(date.getUTCDate() + 1);
    }

    let remaining = this.leadDays;
    while (remaining > 0) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (this.isWorkingDay(date)) remaining -= 1;
    }

    return date;
  }

  isWorkingDay(date) {
    const weekday = date.getUTCDay();
    if (weekday === 0 || weekday === 6) return false;

    return !this.holidaysFor(date.getUTCFullYear()).includes(this.isoDate(date));
  }

  holidaysFor(year) {
    if (this.holidayCache[year]) return this.holidayCache[year];

    const calendar = LabShippingEstimate.HOLIDAYS[this.dataset.holidays] || [];
    const holidays = calendar.map((holiday) => year + '-' + holiday);

    if (calendar.length) {
      const easter = LabShippingEstimate.easterSunday(year);
      const easterMonday = new Date(Date.UTC(year, easter.month - 1, easter.day + 1));
      holidays.push(this.isoDate(easterMonday));
    }

    this.holidayCache[year] = holidays;
    return holidays;
  }

  nowInShopTimezone() {
    const now = new Date();

    try {
      const parts = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone: this.timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23'
      })
        .formatToParts(now)
        .forEach((part) => {
          parts[part.type] = part.value;
        });

      return {
        year: parseInt(parts.year, 10),
        month: parseInt(parts.month, 10),
        day: parseInt(parts.day, 10),
        hour: parseInt(parts.hour, 10) % 24,
        minute: parseInt(parts.minute, 10)
      };
    } catch (error) {
      // An unknown timezone would throw; the visitor's clock is a usable guess.
      return {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
        day: now.getDate(),
        hour: now.getHours(),
        minute: now.getMinutes()
      };
    }
  }

  formatDate(date) {
    const day = this.dayLabels[date.getUTCDay()];
    const month = this.monthLabels[date.getUTCMonth()];
    const dayOfMonth = date.getUTCDate();
    const paddedDay = String(dayOfMonth).padStart(2, '0');
    const paddedMonth = String(date.getUTCMonth() + 1).padStart(2, '0');

    switch (this.dataset.dateFormat) {
      case 'day_dd_month':
        return day + ' ' + dayOfMonth + ' ' + month;
      case 'dd_month':
        return dayOfMonth + ' ' + month;
      case 'dd_month_abbr':
        return dayOfMonth + ' ' + this.abbreviate(month);
      case 'day_dd':
        return day + ' ' + dayOfMonth;
      case 'day_abbr_dd':
        return this.abbreviate(day) + ' ' + dayOfMonth;
      case 'dd_mm_numeric':
        return paddedDay + '/' + paddedMonth;
      default:
        return this.abbreviate(day) + ' ' + dayOfMonth + ' ' + this.abbreviate(month);
    }
  }

  /*
   * Italian shortens both weekdays and months to their first three letters
   * (mercoledì becomes mer, settembre becomes set), which is what keeps the
   * line on a single row on a phone. A shop writing its own labels in another
   * language can pick one of the full-label formats instead.
   */
  abbreviate(label) {
    return label.slice(0, 3);
  }

  isoDate(date) {
    return date.toISOString().slice(0, 10);
  }

  // Meeus/Jones/Butcher algorithm for the Gregorian calendar.
  static easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return { month: month, day: day };
  }
}

LabShippingEstimate.DAY_LABELS = [
  'lunedì',
  'martedì',
  'mercoledì',
  'giovedì',
  'venerdì',
  'sabato',
  'domenica'
];

LabShippingEstimate.MONTH_LABELS = [
  'gennaio',
  'febbraio',
  'marzo',
  'aprile',
  'maggio',
  'giugno',
  'luglio',
  'agosto',
  'settembre',
  'ottobre',
  'novembre',
  'dicembre'
];

// Fixed national holidays as MM-DD; Easter Monday is added per year.
LabShippingEstimate.HOLIDAYS = {
  it: [
    '01-01', // Capodanno
    '01-06', // Epifania
    '04-25', // Festa della Liberazione
    '05-01', // Festa dei Lavoratori
    '06-02', // Festa della Repubblica
    '08-15', // Ferragosto
    '11-01', // Tutti i Santi
    '12-08', // Immacolata Concezione
    '12-25', // Natale
    '12-26' // Santo Stefano
  ]
};

if (!customElements.get('lab-shipping-estimate')) {
  customElements.define('lab-shipping-estimate', LabShippingEstimate);
}
